import { getServerTime } from '@/utils/date-helper';
import { updateStorageMarks } from '@/utils/storage-helper';
import { CustomException, elk } from '@tencent/ppd-base-libs';
import { promQrySubcWxMsg, promSubcWxMsgV2, PromSubcWxMsgV2State } from '@tencent/wphk-act-base-cgi';
import { cacheFunc } from '@tencent/wphk-base-utils';
import { computed, onMounted, reactive } from '@vue/composition-api';
import dayjs from 'dayjs';
import { checkTimeBetween } from '../utils/date-helper';
import { getStorageMarks } from '../utils/storage-helper';
import { to } from '../utils/to';
import { isSupportWxSubscribe } from '../utils/wx-subscribe-util';

const MONTH_TASK = 'month_task'; // 狂赏印
/** 每月展示最大次数 */
// const MAX_VIEW_TIMES = 3;
const getStorageKey = () => `E-STAMP-SUBC-VIEW-${dayjs(getServerTime()).format('YYYYMM')}`;

type ISubscribeConfig = {
  id: string, // 用于给后台请求
  templateId: string, // 用于给微信订阅open tag, 模板id在公众号平台配置
}[];

const SUBSCRIBE_CONFIG: ISubscribeConfig = [
  {
    id: 'subc.task_qual_expire_notify',
    templateId: 'zWIb-QJTVSUz5dLAMbFFSFaFVHDDAKJdoIbUdlsS-MM', // 印花即将失效通知
  },
  {
    id: 'subc.limit_task_online_notify',
    templateId: 'SOe4doDWkQqR2cPxRDlET2G8yHgKzws6FDHvTIB7pEw', // 活动上线通知
  },
  {
    id: 'subc.undo_task_notify',
    templateId: 'ra1JD-3CYsU16qZefAzVzb-mHcEpoyNGmUQmfwchNOo', // 活动进度通知
  },
  {
    id: 'subc.coupon_online_notify',
    templateId: 'D1bFAX_Aa9eGLWCbkFxpUdkZiLgUjTPCiJ4JlYpN01c', // 优惠券更新通知
  },
];

/** 每天不展示微信订阅按钮的时间段 */
const subscribeTimeConfig = {
  disableStartTime: { hours: 11, min: 55, sec: 0 }, // 11:55:00
  disableEndTime: { hours: 12, min: 30, sec: 0 }, // 12:30:00
};

const subscribeConfig = reactive({
  /** 是否已初始化 */
  isInit: false,
  /* 是否灰度组 */
  isGrayGroup: false,
  /* 未订阅的模板id数组 */
  unSubscribeTmpIds: [] as string[],
});

export const useWxSubscribe = () => {
  // 用于展示兑换弹窗时的动态校验
  const checkShouldShowSubscribe = async () => {
    await initSubScribe(); // 每次检测时需要重新获取已订阅模版列表
    const shouldShowWithFrequencyCtl = shouldShowSubscribeWithStorage();
    const isWxSupport = isSupportWxSubscribe();
    const isTimeEnable = isSubcTimeEnable();
    const { isGrayGroup } = subscribeConfig;
    const hasUnSubcTmpIds = subscribeConfig.unSubscribeTmpIds.length > 0;
    const shouldShow = shouldShowWithFrequencyCtl && isWxSupport && isTimeEnable && isGrayGroup && hasUnSubcTmpIds;
    elk({
      key: 'eStampCheckShouldShowSubscribe',
      str1: `shouldShow:${shouldShow}`,
      msg: `shouldShowWithFrequencyCtl:${shouldShowWithFrequencyCtl};isWxSupport:${isWxSupport};isTimeEnable:${isTimeEnable};isGrayGroup${isGrayGroup};hasUnSubcTmpIds:${hasUnSubcTmpIds}`,
    });
    return shouldShow;
  };

  const handleSubscribeRes = async (res) => {
    // 把用户接收的模板id发给后端
    const acceptTemplates = getAcceptTemplates(res);
    if (acceptTemplates.length > 0) {
      await recordWxSubscribe(acceptTemplates);
    }
    // 如果為true，則調用方toast訂閱成功
    return acceptTemplates.length > 0;
  };

  const recordWxSubscribe = async (tmpIds: string[]) => {
    const tplIds = getTmpKeyByIds(tmpIds);
    console.log('======tplIds', tplIds);
    // 静默调用即可
    await to(promSubcWxMsgV2({ chnId: MONTH_TASK, tplIds  }));
  };


  onMounted(() => {
    initSubScribe();
  });

  const unSubscribeTmpIds = computed(() => subscribeConfig.unSubscribeTmpIds);

  return {
    /** 订阅配置 */
    config: subscribeConfig,
    unSubscribeTmpIds,
    /** 处理onSuccess回调 */
    handleSubscribeRes,
    /** 展示前动态校验是否应该展示订阅按钮 */
    checkShouldShowSubscribe,
    /** 展示订阅按钮之后标记到storage中 */
    markSubcribeBtnView,
    /** 更新订阅信息 */
    updateSubScribe,
  };
};

/* 初始化：查灰度组，查订阅模板数 - 緩存函數*/
const initSubScribe = cacheFunc(async () => {
  if (subscribeConfig.isInit) {
    return;
  }
  const [unSubscribeTmpIds, isGrayGroup] = await Promise.all([
    await getUnSubscribeTmpIds(),
    await checkIsGrayGroup(),
  ]);
  subscribeConfig.isGrayGroup = isGrayGroup;
  subscribeConfig.unSubscribeTmpIds = unSubscribeTmpIds;
  subscribeConfig.isInit = true;
});

/** 更新订阅模版信息 */
const updateSubScribe = async () => {
  subscribeConfig.isInit = false;
  initSubScribe.clearCache();
  await initSubScribe();
};

/** 是否能展示订阅按钮-判断缓存 */
const shouldShowSubscribeWithStorage = () =>  true; // 订阅组件需要一直展示，直到全部订阅完成或全部拒绝

/** 标记订阅按钮展示动作 */
const markSubcribeBtnView = () => {
  const marks = getStorageMarks(getStorageKey());
  const curTime = dayjs(getServerTime()).format('YYYYMMDD');
  if (!marks.includes(curTime)) {
    updateStorageMarks(getStorageKey(), [...marks, curTime]);
  }
};

/** 获取未订阅的模板Id列表 */
const getUnSubscribeTmpIds = async () => {
  const [err, res] = await to(promQrySubcWxMsg({ chnId: MONTH_TASK }));
  console.log('======qryWxSubscribe', err, res);
  if (err) {
    return [];
  }
  const resSubcTmpList = res?.list
    .filter(item => item.subcState === PromSubcWxMsgV2State.HAS_SUBSCRIBE)
    .map(item => item.tplId);
  console.log('=====qryWxSubscribe has subc', resSubcTmpList);
  const unSubscribeTmpIds = SUBSCRIBE_CONFIG.filter(config => !resSubcTmpList.includes(config.id))
    .map(item => item.templateId);
  console.log('=====qryWxSubscribe unSubscribeTmpIds', unSubscribeTmpIds);
  return unSubscribeTmpIds;
};

/** 获取灰度配置 并 判断是否为灰度组 */
const checkIsGrayGroup = async () => true;

/** 根据templateId获得对应的id给后端 */
const getTmpKeyByIds = (tmpIds: string[]) => (SUBSCRIBE_CONFIG || [])
  .filter(item => tmpIds.includes(item.templateId))
  .map(config => config.id);


// 获得“已订阅”的模板id
const getAcceptTemplates = (res): string[] => {
  console.log('=====getAcceptTemplates', res);
  const acceptTemplateIds: string[] = [];
  try {
    if (res && /ok/.test(res.errMsg || '')) {
      const subscribeDetails = JSON.parse((res?.subscribeDetails || ''));
      Object.keys(subscribeDetails).forEach(key => {
        const detailItem = JSON.parse(subscribeDetails[key] || '');
        if (key && detailItem?.status === 'accept') {
          // hasAccept = true;
          acceptTemplateIds.push(key);
        }
      });
    }
  } catch (e) {
    new CustomException(e, 'handleSubscribeResException', JSON.stringify(res));
  }
  return acceptTemplateIds;
};

const isSubcTimeEnable = () => {
  const curTime = getServerTime();
  const { disableStartTime, disableEndTime } = subscribeTimeConfig;
  // 开始禁止展示订阅按钮时间
  const todayDisableStartTime = new Date(new Date(curTime).setHours(
    disableStartTime.hours,
    disableStartTime.min,
    disableStartTime.sec,
  ));
  // 结束禁止展示订阅按钮时间
  const todayDisableEndTime = new Date(new Date(curTime).setHours(
    disableEndTime.hours,
    disableEndTime.min,
    disableEndTime.sec,
  ));
  // 是否处于禁止展示时间段内
  const isDisableTimeInBetween = checkTimeBetween({
    targetTime: curTime,
    startDate: todayDisableStartTime,
    endDate: todayDisableEndTime,
  });
  return !isDisableTimeInBetween ;
};
